import { Button, Tooltip } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  RequestForInvoicingStatusEnum,
  useCreateOutgoingInvoiceMutation,
  useCreateRequestForInvoicingMutation,
  useUpdateOutgoingInvoiceMutation,
  useUpdateRequestForInvoicingMutation,
} from "@src/__generated__/graphql";
import { DocumentLimitTypeEnum } from "@src/__generated__/urql-graphql";
import { Banner } from "@src/components/ui-kit";
import { routeHistory } from "@src/helpers/route-history";
import { onError } from "@src/utils/apollo";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React from "react";
import { InvoiceTypeEnum, useInvoiceStore } from "../../store";
import { SubmitForInvoiceButton } from "./SubmitForInvoiceButton";

export const CreateOutgoingButton = observer(function CreateOutgoingButton() {
  const appStore = useStore();
  const store = useInvoiceStore();
  const router = useRouter();

  const showTimeBillingWarning =
    !store.isRFI && store.invoice_id && store.isLinkedToLockedTimeBilling;

  // Document limit check for invoice creation
  const documentLimitType =
    store.type === InvoiceTypeEnum.Proforma
      ? DocumentLimitTypeEnum.ProformaOutgoingInvoice
      : DocumentLimitTypeEnum.OutgoingInvoice;
  const invoiceLimitStatus = appStore.authStore.getDocumentLimitStatus(
    documentLimitType,
    store.sumInvoiceItems /
      (store.form.workspace_exchange_rate.value.value ?? 1),
  );

  const [createOutgoingInvoiceMutation, createOutgoingInvoiceRequest] =
    useCreateOutgoingInvoiceMutation({
      async onCompleted({ createOutgoingInvoice }) {
        if (!createOutgoingInvoice) return;

        appStore.UIStore.toast({
          title: t`Outgoing invoice created`,
          status: "success",
        });

        if (store.isTimeAndMaterialsSelected) {
          await store.timeAndMaterialsStore.saveFiltersAsTemplate(
            createOutgoingInvoice.id,
            "oi",
          );
        }

        router.push({
          pathname: "/invoices/outgoing-invoices/detail",
          query: { id: createOutgoingInvoice.id },
        });
      },
      ...onError(),
    });

  const [
    createRequestForInvoiceAndOutgoingInvoiceMutation,
    createRequestForInvoiceAndOutgoingInvoiceRequest,
  ] = useCreateRequestForInvoicingMutation({
    async onCompleted(data) {
      store.selectedBrandsRefetch?.();

      if (store.isTimeAndMaterialsSelected) {
        await store.timeAndMaterialsStore.saveFiltersAsTemplate(
          data.createRequestForInvoicing.id,
          "oi",
        );
      }

      if (!data.createRequestForInvoicing.workspace_bank_account_id) {
        appStore.UIStore.toast({
          title: t`Outgoing invoice wasn't created because bank account isn't selected.`,
          status: "warning",
        });

        router.push({
          pathname: "/invoices/outgoing-invoices",
        });
        return;
      }

      if (!store.form.invoice_order_sequence_id.$) return;
      createOutgoingInvoiceMutation({
        variables: {
          request_for_invoicing_id: data.createRequestForInvoicing.id,
          skip_date_validations: store.skip_date_validations,
          invoice_no: store.form.invoice_no.$,
          invoice_order_sequence_id: store.form.invoice_order_sequence_id.$,
        },
      });
    },
    ...onError(),
  });

  const [
    updateRequestForInvoiceAndOutgoingInvoiceMutation,
    updateRequestForInvoiceAndOutgoingInvoiceRequest,
  ] = useUpdateRequestForInvoicingMutation({
    async onCompleted(data) {
      store.selectedBrandsRefetch?.();
      if (!data.updateRequestForInvoicing.workspace_bank_account_id) {
        appStore.UIStore.toast({
          title: t`Outgoing invoice wasn't created because bank account isn't selected.`,
          status: "warning",
        });

        router.push({
          pathname: "/invoices/outgoing-invoices",
        });
        return;
      }

      if (store.isTimeAndMaterialsSelected) {
        await store.timeAndMaterialsStore.saveFiltersAsTemplate(
          data.updateRequestForInvoicing.id,
          "oi",
        );
      }

      if (!store.form.invoice_order_sequence_id.$) return;
      createOutgoingInvoiceMutation({
        variables: {
          request_for_invoicing_id: data.updateRequestForInvoicing.id,
          invoice_no: store.form.invoice_no.$,
          skip_date_validations: store.skip_date_validations,
          invoice_order_sequence_id: store.form.invoice_order_sequence_id.$,
        },
      });
    },
    ...onError(),
  });

  const onCreateOutgoingInvoice = async () => {
    const { hasError } = await store.validateAllSteps();
    if (hasError) return;

    if (store.invoice_id) {
      updateRequestForInvoiceAndOutgoingInvoiceMutation({
        variables: {
          input: store.updateRequestForInvoiceInput(
            RequestForInvoicingStatusEnum.Submitted,
          ),
          skip_date_validations: store.skip_date_validations,
        },
      });
    } else {
      createRequestForInvoiceAndOutgoingInvoiceMutation({
        variables: {
          input: store.createRequestForInvoiceInput(
            RequestForInvoicingStatusEnum.Submitted,
          ),
          skip_date_validations: store.skip_date_validations,
        },
      });
    }
  };

  const [updateOutgoingInvoiceMutation, updateOutgoingInvoiceRequest] =
    useUpdateOutgoingInvoiceMutation({
      async onCompleted(data) {
        appStore.UIStore.toast({
          title: t`Invoice updated`,
          status: "success",
        });

        if (store.isTimeAndMaterialsSelected) {
          await store.timeAndMaterialsStore.saveFiltersAsTemplate(
            data.updateOutgoingInvoice.id,
            "oi",
            store.timeAndMaterialsStore.filtersTemplate?.id,
          );
        }

        router.push(routeHistory.get("/invoices/outgoing-invoices"));
      },
      ...onError(),
    });

  const onUpdateOutgoingInvoice = async () => {
    const { hasError } = await store.validateAllSteps();
    if (hasError) return;

    updateOutgoingInvoiceMutation({
      variables: {
        input: store.updateOutgoingInvoiceInput(),
        skip_date_validations: store.skip_date_validations,
      },
    });
  };

  store.setInvoiceMutationLoading(
    createOutgoingInvoiceRequest.loading ||
      createRequestForInvoiceAndOutgoingInvoiceRequest.loading ||
      updateRequestForInvoiceAndOutgoingInvoiceRequest.loading ||
      updateOutgoingInvoiceRequest.loading,
  );

  return (
    <React.Fragment>
      {showTimeBillingWarning && (
        <Banner
          status="warning"
          title={t`This invoice is linked to a Time Billing calculation. If you change the invoice, please make sure to review and adjust the related Time Billing accordingly.`}
          mb="4"
        />
      )}
      {store.isRFI ? (
        store.isFinancier ? (
          <Tooltip
            isDisabled={invoiceLimitStatus.allowed && !store.isLocked}
            label={
              store.isLocked
                ? t`Invoice is locked`
                : invoiceLimitStatus.tooltipLabel
            }
          >
            <Button
              px={{ base: "2", md: "4" }}
              isDisabled={
                !!store.projectOverdue ||
                !invoiceLimitStatus.allowed ||
                store.isLocked
              }
              isLoading={
                store.convertAmountFetcher.isLoading.value ||
                store.exchangeRateFetcher.isLoading.value ||
                store.invoiceMutationLoading
              }
              onClick={onCreateOutgoingInvoice}
            >
              {store.type === InvoiceTypeEnum.CreditNote
                ? t`Create credit note`
                : t`Create invoice`}
            </Button>
          </Tooltip>
        ) : (
          <SubmitForInvoiceButton />
        )
      ) : (
        <Tooltip
          isDisabled={
            store.isFinancier && invoiceLimitStatus.allowed && !store.isLocked
          }
          label={
            !store.isFinancier
              ? t`You don't have permission to update the invoice`
              : store.isLocked
                ? t`Invoice is locked`
                : invoiceLimitStatus.tooltipLabel
          }
        >
          <Button
            px={{ base: "2", md: "4" }}
            isDisabled={
              !store.isFinancier ||
              !!store.projectOverdue ||
              !invoiceLimitStatus.allowed ||
              store.isLocked
            }
            isLoading={
              store.convertAmountFetcher.isLoading.value ||
              store.exchangeRateFetcher.isLoading.value ||
              store.invoiceMutationLoading
            }
            onClick={onUpdateOutgoingInvoice}
          >
            {store.type === InvoiceTypeEnum.CreditNote
              ? t`Update credit note`
              : t`Update invoice`}
          </Button>
        </Tooltip>
      )}
    </React.Fragment>
  );
});
